import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2, Trash2, Users, ShieldCheck, ShieldAlert, Save, Loader2,
} from 'lucide-react'
import { schoolsApi } from '@/lib/api'
import { useAuthStore } from '@/store/auth'
import type { SchoolMember, UserRole } from '@/types'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { roleLabel } from '@/lib/utils'
import DelegationCard from '@/components/DelegationCard'

const ROLES: UserRole[] = [
  'DIRIGENTE', 'DPO', 'DOCENTE', 'SEGRETERIA', 'ISPETTORE',
]

export default function SchoolAdmin() {
  const { user } = useAuthStore()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const isDirigente = user?.role === 'DIRIGENTE'
  const [dpoEmail, setDpoEmail] = useState<string | null>(null)
  const [dpoName, setDpoName] = useState<string | null>(null)
  const [pecCode, setPecCode] = useState('')

  const { data: school, isLoading } = useQuery({
    queryKey: ['my-school'],
    queryFn: schoolsApi.getMine,
  })

  const { data: members, isLoading: membersLoading } = useQuery({
    queryKey: ['school-members'],
    queryFn: schoolsApi.listMembers,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['my-school'] })
    queryClient.invalidateQueries({ queryKey: ['school-members'] })
  }

  const { mutate: changeRole } = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) =>
      schoolsApi.updateMemberRole(id, role),
    onSuccess: () => {
      invalidate()
      toast({ title: 'Ruolo aggiornato' })
    },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const { mutate: removeMember } = useMutation({
    mutationFn: (id: string) => schoolsApi.removeMember(id),
    onSuccess: () => {
      invalidate()
      toast({ title: 'Utente rimosso dall\'istituto' })
    },
    onError: (e: any) =>
      toast({ title: e?.response?.data?.detail || 'Errore', variant: 'destructive' }),
  })

  const { mutate: saveDpo, isPending: savingDpo } = useMutation({
    mutationFn: ({ email, name }: { email: string | null; name: string | null }) =>
      schoolsApi.setDpo(email, name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-school'] })
      setDpoEmail(null)
      setDpoName(null)
      toast({ title: 'DPO aggiornato' })
    },
    onError: (e: any) =>
      toast({
        title: e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || 'Errore',
        variant: 'destructive',
      }),
  })

  if (isLoading) {
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    )
  }

  if (!school) {
    return (
      <div className="p-6 max-w-3xl mx-auto text-center py-16 text-muted-foreground">
        <Building2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
        <p>Non sei associato ad alcun istituto.</p>
        <p className="text-sm mt-1">
          L'associazione avviene automaticamente accedendo con l'account istituzionale.
          Se l'istituto non è ancora attivo, contatta l'amministratore della piattaforma.
        </p>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6 stagger">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Building2 className="w-6 h-6 text-mim-blue" />
          Il mio Istituto
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          {school.name}
          {school.codice_meccanografico && ` · ${school.codice_meccanografico}`}
          {school.city && ` · ${school.city}`}
        </p>
      </div>

      {isDirigente && (
        <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-mim-blue" />
          <p>
            Come Dirigente designi il <strong>DPO</strong>, gestisci i ruoli dei membri e
            autorizzi le valutazioni. I dati dell'istituto, il dominio e l'attivazione del
            piano sono gestiti dall'amministratore della piattaforma.
          </p>
        </div>
      )}

      {/* DPO designation (Dirigente only) */}
      {isDirigente && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-mim-blue" />
              Responsabile Protezione Dati (DPO)
            </CardTitle>
            <CardDescription className="text-xs">
              Il DPO valida tecnicamente gli strumenti IA prima della tua autorizzazione.
              Indica la sua email (può essere condiviso con altri istituti e cambiato nel
              tempo). Senza un DPO nessuno strumento può essere autorizzato.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Email del DPO</Label>
                <Input
                  type="email"
                  className="mt-1"
                  placeholder="dpo@esempio.it"
                  value={dpoEmail ?? school.dpo_email ?? ''}
                  onChange={(e) => setDpoEmail(e.target.value)}
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Nome e cognome del DPO</Label>
                <Input
                  className="mt-1"
                  placeholder="Es. Maria Rossi"
                  value={dpoName ?? school.dpo_name ?? ''}
                  onChange={(e) => setDpoName(e.target.value)}
                />
              </div>
              <Button
                onClick={() => saveDpo({
                  email: (dpoEmail ?? school.dpo_email ?? '').trim() || null,
                  name: (dpoName ?? school.dpo_name ?? '').trim() || null,
                })}
                disabled={savingDpo || (dpoEmail === null && dpoName === null)}
                className="bg-mim-blue hover:bg-blue-800 text-white"
              >
                {savingDpo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Il nome compare come «Valutatore» nel report PDF finché il DPO non
              accede con un proprio account.
            </p>
            {!school.dpo_email && (
              <p className="text-xs text-rosso mt-2 flex items-center gap-1.5">
                <ShieldAlert className="w-3.5 h-3.5" /> Nessun DPO designato: le valutazioni non possono essere autorizzate.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Verifica identità via IPA + PEC (P8) — solo DS */}
      {isDirigente && school && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-mim-blue" />
              Verifica dell'istituto (IPA + PEC)
            </CardTitle>
            <CardDescription className="text-xs">
              I dati ufficiali provengono dall'Indice PA; il codice di conferma viene
              inviato alla PEC istituzionale risultante dall'IPA, non a un indirizzo
              dichiarato.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {school.ipa_verified_at ? (
              <p className="text-sm flex items-center gap-2">
                <Badge className="bg-verde text-white text-xs">Istituto verificato</Badge>
                <span className="text-muted-foreground text-xs">
                  via PEC {school.pec_email} il{' '}
                  {new Date(school.ipa_verified_at).toLocaleDateString('it-IT')}
                </span>
              </p>
            ) : school.pec_email ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Un codice di verifica è stato (o sarà) inviato a{' '}
                  <b>{school.pec_email}</b>. Inseriscilo qui per completare la verifica.
                </p>
                <div className="flex gap-2 max-w-sm">
                  <Input
                    value={pecCode}
                    onChange={(e) => setPecCode(e.target.value)}
                    placeholder="Codice a 8 cifre"
                    className="h-9"
                  />
                  <Button
                    size="sm"
                    className="bg-mim-blue hover:bg-blue-800 text-white"
                    disabled={pecCode.trim().length < 6}
                    onClick={async () => {
                      try {
                        await schoolsApi.verifyPec(pecCode.trim())
                        queryClient.invalidateQueries({ queryKey: ['my-school'] })
                        toast({ title: 'Istituto verificato' })
                        setPecCode('')
                      } catch (err: any) {
                        toast({
                          title: 'Verifica non riuscita',
                          description: err?.response?.data?.detail ?? 'Errore imprevisto',
                          variant: 'destructive',
                        })
                      }
                    }}
                  >
                    Verifica
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                La verifica sarà attivata dall'amministratore della piattaforma
                (lookup IPA e invio del codice alla PEC istituzionale).
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Members */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-mim-blue" />
            Membri {members ? `(${members.length})` : ''}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {membersLoading ? (
            <div className="space-y-2">
              {Array(3).fill(0).map((_, i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
            </div>
          ) : (
            <div className="space-y-2">
              {members?.map((m: SchoolMember) => {
                const isSelf = m.id === user?.id
                return (
                  <div
                    key={m.id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border"
                  >
                    <Avatar className="w-9 h-9">
                      <AvatarImage src={m.avatar_url} />
                      <AvatarFallback className="bg-mim-blue text-white text-xs">
                        {m.name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {m.name}
                        {isSelf && <span className="text-muted-foreground font-normal"> (tu)</span>}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">{m.email}</p>
                    </div>
                    {isDirigente && !isSelf ? (
                      <>
                        <Select
                          value={m.role}
                          onValueChange={(role) => changeRole({ id: m.id, role })}
                        >
                          <SelectTrigger className="w-44 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => (
                              <SelectItem key={r} value={r} className="text-xs">
                                {roleLabel(r)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive hover:bg-destructive/10"
                          title="Rimuovi dall'istituto"
                          onClick={() => {
                            if (confirm(`Rimuovere ${m.name} dall'istituto?`)) {
                              removeMember(m.id)
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <Badge variant="outline" className="text-xs">{roleLabel(m.role)}</Badge>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delegation (P7): DS only */}
      {isDirigente && members && <DelegationCard members={members} />}
    </div>
  )
}
